import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
} from "@mui/material";
import axios from "axios";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 520,
  maxWidth: "92vw",
  maxHeight: "85vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: 24,
  p: 4,
};

const formatCurrency = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? `$${number.toFixed(2)}` : "$0.00";
};

const SummaryCard = ({ label, value }) => (
  <Paper variant="outlined" sx={{ p: 1.25 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="subtitle1">{value}</Typography>
  </Paper>
);

const AddToExistingPOModal = ({ open, onClose, batchId, selectedRows = [], summary }) => {
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [selectedPO, setSelectedPO] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (open) {
      axios
        .get("/api/purchase_orders/list")
        .then((res) => setPurchaseOrders(res.data))
        .catch(() =>
          setSnackbar({ open: true, message: "Failed to fetch purchase orders.", severity: "error" })
        );
    }
  }, [open]);

  const handleSubmit = () => {
    if (!selectedPO) return;

    setLoading(true);
    axios
      .post("/api/purchase_requests/add_to_existing_po", {
        batch_id: batchId,
        purchase_order_id: selectedPO,
        purchase_request_ids: selectedRows.map((row) => row.id),
      })
      .then(() => {
        setSnackbar({ open: true, message: "PRs added to PO successfully!", severity: "success" });
        onClose();
      })
      .catch(() => {
        setSnackbar({ open: true, message: "Failed to add PRs to PO.", severity: "error" });
      })
      .finally(() => setLoading(false));
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Typography variant="h6" gutterBottom>
          Add Accepted PRs to Existing PO
        </Typography>
        <Typography variant="body2" gutterBottom>
          Purchase Request Batch ID: {batchId}
        </Typography>

        <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 1.25 }}>
          <SummaryCard label="Selected Rows" value={summary?.selectedCount || selectedRows.length} />
          <SummaryCard label="Offer Total" value={formatCurrency(summary?.offerTotal)} />
          <SummaryCard label="Predicted Total" value={formatCurrency(summary?.predictedTotal)} />
          <SummaryCard label="Projected Profit" value={formatCurrency(summary?.projectedProfit)} />
        </Box>

        <FormControl fullWidth margin="normal">
          <InputLabel>Select PO</InputLabel>
          <Select
            value={selectedPO}
            onChange={(e) => setSelectedPO(e.target.value)}
            label="Select PO"
          >
            {purchaseOrders.map((po) => (
              <MenuItem key={po.id} value={po.id}>
                {po.supplier_name} (PO #{po.id})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button onClick={onClose} color="secondary" sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !selectedPO}
          >
            {loading ? <CircularProgress size={24} /> : "Add to PO"}
          </Button>
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Modal>
  );
};

export default AddToExistingPOModal;
